// AuthManager.js

import { EventDispatcher } from "../../services/EventDispatcher.js";

export class AuthManager {

    /**
     * @param {Object} config
     * @param {RemoteService} config.authService          - RemoteService instance pro HTTP volání
     * @param {'cookie'|'bearer'} config.authMethod          - metoda autentizace
     * @param {Storage|null} [config.storage]               - např. window.localStorage; pokud null, nic se nepersistuje
     * @param {string} [config.storageKey='auth']           - klíč v úložišti
     * @param {string} [config.loginPath='/auth/login']
     * @param {string} [config.logoutPath='/auth/logout']
     * @param {string} [config.refreshPath='/auth/refresh']
     * @param {string} [config.userPath='/auth/user']
     */
    constructor(config = {}) {

        const {
            authService,
            authMethod = 'cookie',
            storage = typeof window !== 'undefined' ? window.localStorage : null,
            storageKey = 'auth',
            loginPath = '/auth/login',
            logoutPath = '/auth/logout',
            refreshPath = '/auth/refresh',
            userPath = '/auth/user'
        } = config;

        if (!authService) throw new Error('AuthManager: "authService" (RemoteService) is required.');

        this.authService = authService;
        this.authMethod = authMethod;
        this.storage = storage;
        this.storageKey = storageKey;
        this.loginPath = loginPath;
        this.logoutPath = logoutPath;
        this.refreshPath = refreshPath;
        this.userPath = userPath;

        this.accessToken = null;
        this.refreshToken = null;
        this.expiresAt = null; // timestamp (ms) nebo null
        this.currentUser = null;

        // event handlers
        this._loginEvent = new EventDispatcher();
        this.onLogin = this._loginEvent.subscribeHandler();
        this._logoutEvent = new EventDispatcher();
        this.onLogout = this._logoutEvent.subscribeHandler();
        this._authErrorEvent = new EventDispatcher();
        this.onAuthError = this._authErrorEvent.subscribeHandler();
        this._tokenRefreshedEvent = new EventDispatcher();
        this.onTokenRefreshed = this._tokenRefreshedEvent.subscribeHandler();

        // shared promise pro current refresh
        this._refreshPromise = null;

        this._loadFromStorage();
        this._initializeStorageSync();
    }

    // -----------------------------------------------------
    // Public API
    // -----------------------------------------------------

    /**
     * Přihlášení uživatele.
     * @param {Object} credentials - typicky { username, password } nebo podle API
     * @returns {Promise<Object>} - user object (pokud ho API vrací)
     */
    async login(credentials) {

        const response = await this.authService.post(this.loginPath, credentials, { responseType: 'json' });

        if (!response.ok) {
            // očekáváš-li chybové zprávy v JSON, můžeš je vytáhnout z response.data
            const error = new Error('Login failed');
            error.status = response.status;
            error.body = response.data ?? response.rawBody;
            throw error;
        }

        if (this.isBearerMode()) {

            const { authToken, refreshToken, expiresAt, user } = this._extractTokensFromLogin(response.data);

            this._setAuth(authToken, refreshToken, expiresAt, user);

            this._loginEvent.dispatch(user);

            return user;

        }

        let user = response.data?.user ?? null;
        if (!user) {
            user = await this.refreshUser(true);
        } else {
            this.currentUser = user;
            this._saveToStorage();
        }

        this._loginEvent.dispatch({
            user,
            remote: false,
        });

        return user;
    }

    /**
     * Odhlášení uživatele – smaže tokeny + user info a notifikuje posluchače.
     */
    async logout(options = {}) {

        const {
            remote = false,
        } = options;

        if (!remote) {
            try {
                // do not wait
                this.authService.post(this.logoutPath);
            } catch {
                // consume
            }
        }

        this._clearAuth();
        this._logoutEvent.dispatch({
            remote,
        });

    }

    /**
     * Vrátí true, pokud máme accessToken a ještě neexpiroval (pokud evidujeme expiresAt).
     */
    hasValidToken() {

        if (!this.accessToken) return false;
        if (!this.expiresAt) return true;

        return Date.now() < this.expiresAt;

    }

    /**
     * Zda je uživatel považován za přihlášeného.
     */
    isAuthenticated() {

        if (this.isCookieMode()) {
            // token nevidíš -> neřešíš
            return this.currentUser != null;
        }

        return this.hasValidToken();

    }

    getAccessToken() {

        return this.accessToken;

    }

    getRefreshToken() {

        return this.refreshToken;

    }

    getUser() {

        if (!this.isAuthenticated()) {
            return null;
        }
        return this.currentUser;

    }

    /**
     * Zajistí platný accessToken:
     *  - pokud je platný → vrátí ho
     *  - jinak se pokusí o refresh
     *  - pokud refresh selže → vyhodí chybu
     */
    async ensureValidToken() {

        if (this.hasValidToken()) {
            return this.accessToken;
        }

        await this.refresh();
        return this.accessToken;

    }

    /**
     * Volá /auth/refresh (nebo jiný konfigurovaný endpoint) a aktualizuje tokeny.
     * Řeší souběh více volání – probíhá vždy jen jeden refresh.
     *
     * @returns {Promise<string>} - nový accessToken
     */
    async refresh() {

        if (this.isCookieMode()) {

            // cookie režim – refresh obvykle nepotřebuje token v body
            // (server si najde refresh cookie sám)

            if (this._refreshPromise) return this._refreshPromise;

            this._refreshPromise = this._doCookieRefresh()
                .catch(err => {
                    if (this._isSessionInvalidError(err)) {
                        awaitPromiseSafe(() => this.logout());
                    }
                    this._authErrorEvent.dispatch(err);
                    throw err;
                })
                .finally(() => {
                    this._refreshPromise = null;
                });

            return this._refreshPromise;

        }

        if (!this.refreshToken) {
            // není co refreshnout
            this.logout();
            const err = new Error('No refresh token available.');
            this._authErrorEvent.dispatch(err);
            throw err;
        }

        // pokud refresh už běží, vrátíme existující Promise
        if (this._refreshPromise) {
            return this._refreshPromise;
        }

        this._refreshPromise = this._doRefresh()
            .catch(err => {
                if (this._isSessionInvalidError(err)) {
                    this.logout();
                }
                this._authErrorEvent.dispatch(err);
                throw err;
            })
            .finally(() => {
                this._refreshPromise = null;
            });

        return this._refreshPromise;
    }

    /**
     * Vrátí info o uživateli.
     * Pokud už ho máme v paměti a forceReload = false, vrátí cache.
     * Pokud ne, zavolá user endpoint.
     */
    async refreshUser(forceReload = false) {

        if (!forceReload && this.currentUser) {
            return this.currentUser;
        }

        const authOptions = this.isCookieMode()
            ? { type: 'cookie' }
            : { type: 'bearer', token: this.getAccessToken() };

        const response = await this.authService.get(this.userPath, null, { responseType: 'json', auth: authOptions });

        if (!response.ok) {
            const err = new Error('Failed to load user info.');
            err.status = response.status;
            err.body = response.data ?? response.rawBody;
            if (this._isSessionInvalidError(err)) {
                awaitPromiseSafe(() => this.logout());
            }
            this._authErrorEvent.dispatch(err);
            throw err;
        }

        this.currentUser = response.payload || null;
        this._saveToStorage();
        return this.currentUser;

    }

    validateUserPrivileges(requestedPrivileges, item, ownerIdHandler, parentOwnerIdHandler) {

        var user = this.getUser();

        if (!user)
            return false;

        if (user.isSysAdmin === true)
            return true;

        var privileges = user.privileges || [];
        if (!Array.isArray(privileges))
            privileges = [privileges];

        var userid = user.id;

        requestedPrivileges = requestedPrivileges || [];
        if (!Array.isArray(requestedPrivileges))
            requestedPrivileges = [requestedPrivileges];
        if (requestedPrivileges.length === 0)
            return false;

        return requestedPrivileges.some(function (requestedPrivilege) {
            if (Array.isArray(requestedPrivilege)) {
                return requestedPrivilege.every(function (singleRequestedPrivilege) {
                    if (privileges.includes(singleRequestedPrivilege))
                        return true;
                    if (singleRequestedPrivilege === '#owner') {
                        var ownerId = (item && ownerIdHandler && ownerIdHandler(item)) || null;
                        return ownerId === userid;
                    }
                    if (singleRequestedPrivilege === '#parentOwner') {
                        var parentOwnerId = (item && parentOwnerIdHandler && parentOwnerIdHandler(item)) || null;
                        return parentOwnerId === userid;
                    }
                    return false;
                });
            }
            return privileges.includes(requestedPrivilege);
        });

    };

    hasUserRole(roles) {

        let user = this.getUser();

        if (!user)
            return false;

        if (!Array.isArray(roles))
            roles = [roles]

        return user.roles.some(role => roles.includes(role));

    };

    // -----------------------------------------------------
    // Interní logika
    // -----------------------------------------------------

    isBearerMode() { return this.authMethod === 'bearer'; }

    isCookieMode() { return this.authMethod === 'cookie'; }

    _isSessionInvalidError(error) {
        return [401, 403].includes(error?.status);
    }

    _extractTokensFromLogin(data) {

        // Tady případně přizpůsobíš svému API.
        // Očekávám { accessToken, refreshToken, expiresAt?, user? }

        if (!data || !data.accessToken) {
            throw new Error('Login response does not contain accessToken.');
        }

        return {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? null,
            // předpokládáme, že expiresAt je buď timestamp (ms), nebo ISO string
            expiresAt: data.expiresAt ? this._normalizeExpiresAt(data.expiresAt) : null
        };
    }

    _extractTokensFromRefresh(data) {
        // Opět přizpůsobitelné tvému API.
        if (!data || !data.accessToken) {
            throw new Error('Refresh response does not contain accessToken.');
        }

        return {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? this.refreshToken,
            expiresAt: data.expiresAt
                ? this._normalizeExpiresAt(data.expiresAt)
                : null
        };
    }

    _normalizeExpiresAt(value) {
        if (typeof value === 'number') {
            // předpokládáme ms
            return value;
        }

        // jinak zkusíme Date.parse
        const ts = Date.parse(value);
        return Number.isNaN(ts) ? null : ts;
    }

    async _doRefresh() {

        const payload = { refreshToken: this.refreshToken };

        const response = await this.authService.post(this.refreshPath, payload, {
            responseType: 'json'
        });

        if (!response.ok) {
            const err = new Error('Refresh token request failed.');
            err.status = response.status;
            err.body = response.data ?? response.rawBody;
            throw err;
        }

        const { accessToken, refreshToken, expiresAt } = this._extractTokensFromRefresh(response.data);

        this.currentUser = await this.refreshUser(true);
        this._setAuth(accessToken, refreshToken, expiresAt, this.currentUser);
        this._tokenRefreshedEvent.dispatch(accessToken);

        return accessToken;

    }

    async _doCookieRefresh() {

        const response = await this.authService.post(this.refreshPath, null, { responseType: 'json' });

        if (!response.ok) {
            const err = new Error('Cookie refresh failed.');
            err.status = response.status;
            err.body = response.data ?? response.rawBody;
            throw err;
        }

        // často refresh endpoint nic nevrací; po refreshi si dotáhni user
        this.currentUser = await this.refreshUser(true);
        this._tokenRefreshedEvent.dispatch(null);

        return null;

    }

    _setAuth(accessToken, refreshToken, expiresAt, user = null) {
        this.accessToken = accessToken;
        this.refreshToken = refreshToken;
        this.expiresAt = expiresAt;
        this.currentUser = user ?? this.currentUser;

        this._saveToStorage();
    }

    _clearAuth() {
        this.accessToken = null;
        this.refreshToken = null;
        this.expiresAt = null;
        this.currentUser = null;

        this._clearStorage();
    }

    _loadFromStorage() {
        if (!this.storage) return;

        try {
            const raw = this.storage.getItem(this.storageKey);
            if (!raw) return;

            const data = JSON.parse(raw);
            this.accessToken = data.accessToken ?? null;
            this.refreshToken = data.refreshToken ?? null;
            this.expiresAt = data.expiresAt ?? null;
            this.currentUser = data.currentUser ?? null;
        } catch {
            // ignorujeme chyby
        }
    }

    _saveToStorage() {
        if (!this.storage) return;

        const data = {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expiresAt: this.expiresAt,
            currentUser: this.currentUser
        };

        try {
            this.storage.setItem(this.storageKey, JSON.stringify(data));
        } catch {
            // ignorujeme, typicky quota exceeded apod.
        }
    }

    _clearStorage() {
        if (!this.storage) return;
        try {
            this.storage.removeItem(this.storageKey);
        } catch {
            // ignorujeme
        }
    }

    _initializeStorageSync() {

        if (typeof window === 'undefined')
            return;
        if (!this.storage)
            return;
        if (typeof window.addEventListener !== 'function')
            return;

        window.addEventListener('storage', (event) => {

            if (event.storageArea && event.storageArea !== this.storage)
                return;
            if (event.key !== this.storageKey)
                return;

            this._synchronizeFromStorageEvent(event.newValue ?? null);

        });

    }

    _synchronizeFromStorageEvent(rawValue) {

        const previousSnapshot = this._getAuthSnapshot();
        const nextSnapshot = this._parseStoredSnapshot(rawValue);

        this.accessToken = nextSnapshot.accessToken;
        this.refreshToken = nextSnapshot.refreshToken;
        this.expiresAt = nextSnapshot.expiresAt;
        this.currentUser = nextSnapshot.currentUser;

        this._dispatchStorageSyncEvents(previousSnapshot, nextSnapshot);

    }

    _parseStoredSnapshot(rawValue) {

        if (!rawValue) {
            return {
                accessToken: null,
                refreshToken: null,
                expiresAt: null,
                currentUser: null,
            };
        }

        try {
            const data = JSON.parse(rawValue);
            return {
                accessToken: data.accessToken ?? null,
                refreshToken: data.refreshToken ?? null,
                expiresAt: data.expiresAt ?? null,
                currentUser: data.currentUser ?? null,
            };
        } catch {
            return {
                accessToken: null,
                refreshToken: null,
                expiresAt: null,
                currentUser: null,
            };
        }

    }

    _getAuthSnapshot() {
        return {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expiresAt: this.expiresAt,
            currentUser: this.currentUser,
            isAuthenticated: this.isAuthenticated(),
        };
    }

    _dispatchStorageSyncEvents(previousSnapshot, nextSnapshot) {

        const previousUserId = previousSnapshot.currentUser?.id ?? null;
        const nextUserId = nextSnapshot.currentUser?.id ?? null;
        const wasAuthenticated = previousSnapshot.isAuthenticated;
        const isAuthenticated = this.isAuthenticated();

        if (wasAuthenticated && !isAuthenticated) {
            this._logoutEvent.dispatch({ remote: true });
            return;
        }

        if (!wasAuthenticated && isAuthenticated) {
            this._loginEvent.dispatch({
                user: this.currentUser,
                remote: true,
            });
            return;
        }

        if (
            isAuthenticated
            && (
                previousUserId !== nextUserId
                || previousSnapshot.accessToken !== nextSnapshot.accessToken
                || previousSnapshot.refreshToken !== nextSnapshot.refreshToken
                || previousSnapshot.expiresAt !== nextSnapshot.expiresAt
            )
        ) {
            this._tokenRefreshedEvent.dispatch({
                accessToken: this.accessToken,
                remote: true,
                user: this.currentUser,
            });
        }

    }

    _removeHandler(list, handler) {
        const idx = list.indexOf(handler);
        if (idx >= 0) {
            list.splice(idx, 1);
        }
    }

}

function awaitPromiseSafe(fn) {
    try { return Promise.resolve(fn()); } catch { return Promise.resolve(); }
}
